import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { authAPI, tenantSettingsAPI } from '@/services/api';

// Default styling constants
const DEFAULT_BG_COLOR = '#4a90e2';
const DEFAULT_LOGO_URL = 'https://rallyon.app/logo192.png';
const DEFAULT_HERO_URL = 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1675&q=80';
const DEFAULT_TITLE = 'RallyOn';
const DEFAULT_SUBTITLE = 'Streamline your club operations and enhance member experience';

// Validation schema
const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
});

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState<boolean>(false);
  const [verifying, setVerifying] = useState<boolean>(true);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [title, setTitle] = useState<string>(DEFAULT_TITLE);
  const [subtitle, setSubtitle] = useState<string>(DEFAULT_SUBTITLE);
  const [logoUrl, setLogoUrl] = useState<string>(DEFAULT_LOGO_URL);
  const [heroUrl, setHeroUrl] = useState<string>(DEFAULT_HERO_URL);
  const [bgColor, setBgColor] = useState<string>(DEFAULT_BG_COLOR);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Fetch public tenant settings to customize the page
    async function fetchSettings() {
      if (import.meta.env.VITE_DEPLOYMENT_MODE === 'standalone' && import.meta.env.VITE_TENANT_NAME) {
        try {
          const response = await tenantSettingsAPI.getPublicSettings(import.meta.env.VITE_TENANT_NAME);
          
          const background_colour = response?.data?.tenant_background_colour || DEFAULT_BG_COLOR;
          const logo_url = response?.data?.tenant_logo_url || DEFAULT_LOGO_URL;
          const hero_url = response?.data?.tenant_hero_url || DEFAULT_HERO_URL;
          const title = response?.data?.tenant_heading_title || DEFAULT_TITLE;
          const subtitle = response?.data?.tenant_heading_subtitle || DEFAULT_SUBTITLE;

          setBgColor(background_colour);
          setLogoUrl(logo_url);
          setHeroUrl(hero_url);
          setTitle(title);
          setSubtitle(subtitle);
        } catch (error) {
          console.error('Error fetching tenant settings:', error);
        }
      } 
    }

    fetchSettings();
  }, []);

  useEffect(() => {
    // Verify token when component mounts
    const verifyToken = async () => {
      if (!token) {
        setError('No reset token provided');
        setVerifying(false);
        return;
      }

      try {
        const response = await authAPI.verifyResetToken(token);
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.userEmail || '');
        } else {
          setError(response.data.message || 'Invalid or expired reset token');
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Invalid or expired reset token';
        setError(errorMessage);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values) => {
      if (!token) {
        setError('No reset token provided');
        return;
      }

      setLoading(true);
      setError('');

      try {
        await authAPI.resetPassword(token, values.password, values.confirmPassword);
        setSuccess(true);
      } catch (err: any) {
        let errorMessage = 'Failed to reset password. Please try again.';

        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Show loading while verifying token
  if (verifying) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: bgColor ? `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}CC 100%)` : 'linear-gradient(135deg, #868CFF 0%, #4318FF 100%)',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: '20px',
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            textAlign: 'center'
          }}
        >
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6">Verifying reset token...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: bgColor ? `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}CC 100%)` : 'linear-gradient(135deg, #868CFF 0%, #4318FF 100%)',
      }}
    >
      <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ 
          width: '100%', 
          maxWidth: { xs: '100%', sm: '500px', md: '800px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Left side - Banner/Image (hidden on mobile) */}
          {!isMobile && (
            <Box sx={{ flex: '1', p: 4, color: 'white', textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" mb={3}>
                {title}
              </Typography>
              <Typography variant="h6" mb={4}>
                {subtitle}
              </Typography>
              <Box
                component="img"
                src={heroUrl || DEFAULT_HERO_URL}
                alt="Hero Image"
                sx={{
                  width: '90%',
                  maxHeight: '400px',
                  objectFit: 'cover',
                  borderRadius: '20px',
                  boxShadow: '0 20px 27px 0 rgba(0, 0, 0, 0.05)'
                }}
              />
            </Box>
          )}

          {/* Right side - Reset Password Form */}
          <Box sx={{ flex: '1' }}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 20px 27px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              {/* Back to Login Link */}
              <Box sx={{ mb: 2 }}>
                <Link 
                  component={RouterLink} 
                  to="/login" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    textDecoration: 'none',
                    color: 'primary.main',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <ArrowBack sx={{ mr: 1, fontSize: 18 }} />
                  Back to Login
                </Link>
              </Box>

              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {success ? 'Password Reset!' : 'Reset Your Password'}
                </Typography>
                {!success && (
                  <Typography variant="body1" color="text.secondary" mt={1}>
                    {userEmail && `Reset password for ${userEmail}`}
                  </Typography>
                )}
              </Box>

              {!tokenValid && !success ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error || 'Invalid or expired reset token'}
                  </Alert>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    The password reset link may have expired or is invalid. 
                    Please request a new password reset.
                  </Typography>

                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/forgot-password"
                    sx={{ mr: 2 }}
                  >
                    Request New Reset
                  </Button>

                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to="/login"
                  >
                    Back to Login
                  </Button>
                </Box>
              ) : success ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Your password has been reset successfully! You can now sign in with your new password.
                  </Alert>

                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/login"
                    size="large"
                    sx={{
                      py: 1.5,
                      px: 4,
                      borderRadius: 2,
                      fontWeight: 'bold',
                    }}
                  >
                    Sign In
                  </Button>
                </Box>
              ) : (
                <Box component="form" onSubmit={formik.handleSubmit}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <Box mb={3}>
                    <Typography variant="subtitle2" fontWeight="medium" mb={1}>
                      New Password*
                    </Typography>
                    <TextField
                      fullWidth
                      name="password"
                      placeholder="Enter your new password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && formik.errors.password}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 2,
                          bgcolor: 'background.paper'
                        }
                      }}
                    />
                  </Box>

                  <Box mb={4}>
                    <Typography variant="subtitle2" fontWeight="medium" mb={1}>
                      Confirm New Password*
                    </Typography>
                    <TextField
                      fullWidth
                      name="confirmPassword"
                      placeholder="Confirm your new password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                      helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={handleToggleConfirmPasswordVisibility}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 2,
                          bgcolor: 'background.paper'
                        }
                      }}
                    />
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      boxShadow: '0 4px 7px -1px rgba(0,0,0,0.11), 0 2px 4px -1px rgba(0,0,0,0.07)',
                      mb: 3
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Reset Password'}
                  </Button>

                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Remember your password?{' '}
                      <Link component={RouterLink} to="/login" color="primary">
                        Sign in here
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPassword;